// (C) Copyright 2025 Hewlett Packard Enterprise Development LP

import { Box } from 'grommet'
import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import lodashGet from 'lodash/get'
import { useFlags } from 'launchdarkly-react-client-sdk'

import { ObjectList } from '../../../../../../components'
import { WizardContext } from '../../../../../../components/wizard/WizardContext'
import { renameKeys } from '../../../../../../utils/common-utils'
import {
  idp,
  ssoModeEnum,
  getAuthorizationText,
  getAuthentication,
  mspOptions,
  oidcauthorization,
  generateRedirectUrl
} from '../../../constants'
import { isMSP, isV2 } from '../../../../../../utils/feature-flag-utils'

const ReviewAndCreate = () => {
  const { formValues } = useContext(WizardContext)
  const { t } = useTranslation(['iam'])
  const AuthenticationMode = lodashGet(formValues, 'general.Authnoptions')
  const LDFlags = useFlags()
  const isGlcpOidc = LDFlags['glcp-oidc']
  const getMspLdText = (values) => {
    let text = ''
    const mspOrg = values?.includes(mspOptions.mspOrg)
    const customerOrg = values?.includes(mspOptions.custOrg)
    switch (true) {
      case mspOrg && customerOrg:
        text = 'MSP and customer organizations'
        return text
      case mspOrg:
        text = 'MSP organization'
        return text
      case customerOrg:
        text = 'Customer organizations'
        return text
      default:
        break
    }

    return text
  }

  const getAuthoriration = (value) => {
    let text = ''
    switch (value) {
      case oidcauthorization.LOCAL:
        text = t('iam:sso_profiles.view_edit_create.local_role_assignments')
        return text
      case oidcauthorization.SSO:
        text = t('iam:sso_profiles.view_edit_create.sso_role_assignments')
        return text
      default:
        break
    }
    return text
  }

  let appliesToText
  if (
    formValues.general?.msp_methods?.includes(
      ssoModeEnum.AUTHORIZATION_MSP_MANAGED_TENANT_ONLY
    ) ||
    formValues.general?.msp_methods?.includes(
      ssoModeEnum.AUTHENTICATION_ONLY_MSP_MANAGED_TENANT_ONLY
    )
  ) {
    appliesToText = t(
      'iam:sso_profiles.view_edit_create.customer_organizations'
    )
  } else if (
    formValues.general?.msp_methods?.includes(
      ssoModeEnum.AUTHORIZATION_MSP_MANAGED
    ) ||
    formValues.general?.msp_methods?.includes(
      ssoModeEnum.AUTHENTICATION_ONLY_MSP_MANAGED
    )
  ) {
    appliesToText = t(
      'iam:sso_profiles.view_edit_create.msp_and_customer_organizations'
    )
  } else {
    appliesToText = t('iam:sso_profiles.view_edit_create.msp_organization')
  }

  const tempI18NKeys = {
    general: {
      profileName: t('iam:sso_profiles.view_edit_create.profile_name'),
      ...(isMSP() &&
        isV2() && {
          applies: t('iam:sso_profiles.view_edit_create.applies_to')
        }),
      domain: t('iam:sso_profiles.view_edit_create.domain'),
      authorization: t('iam:sso_profiles.view_edit_create.authorization')
    },
    // will use this when isGlcpOidc is true
    general_LDflag: {
      profileName: t('iam:sso_profiles.view_edit_create.profile_name'),
      domain: t('iam:sso_profiles.view_edit_create.domain'),
      authentication: t('iam:sso_profiles.view_edit_create.authentication'),
      authorization: t('iam:sso_profiles.view_edit_create.authorization'),
      ...(isMSP() && {
        applies: t('iam:sso_profiles.view_edit_create.applies_to')
      })
    },
    samlAttributes: {
      email: t('iam:sso_profiles.view_edit_create.email_address'),
      first_name: t('iam:sso_profiles.view_edit_create.first_name'),
      last_name: t('iam:sso_profiles.view_edit_create.last_name'),
      ccs_attribute: t('iam:sso_profiles.view_edit_create.ccs_attribute')
    },
    openidConnectConfiguration: {
      client: t('iam:sso_profiles_oidc.client_label'),
      usePkce: t('iam:sso_profiles_oidc.use_pkce_label'),
      issuer: t('iam:sso_profiles_oidc.issuer_url_label'),
      authorizationEndpoint: t(
        'iam:sso_profiles_oidc.authorization_endpoint_label'
      ),
      tokenEndpoint: t('iam:sso_profiles_oidc.token_endpoint_label'),
      jwksEndpoint: t('iam:sso_profiles_oidc.jwks_endpoint_label'),
      userInfoEndpoint: t('iam:sso_profiles_oidc.userinfo_endpoint'),
      greenlakeRedirectUrl: t(
        'iam:sso_profiles_oidc.greenlake_redirect_url_label'
      )
    },
    identityProviderConfiguration: {
      entityId: t('iam:sso_profiles.view_edit_create.greenlake_entity_id'),
      acsUrl: t('iam:sso_profiles.view_edit_create.acs_url'),
      defaultRelayState: t(
        'iam:sso_profiles.view_edit_create.default_relay_state'
      )
    },
    greenLakeConfiguration: {
      entity_id: t('iam:sso_profiles.view_edit_create.idp_entity_id'),
      login_url: t('iam:sso_profiles.view_edit_create.login_url'),
      logout_url: t('iam:sso_profiles.view_edit_create.logout_url'),
      signing_certificate: t('iam:sso_profiles.view_edit_create.certificate')
    },
    recoveryUser: {
      recoveryEmail: t('iam:sso_profiles.view_edit_create.recovery_username'),
      pocEmail: t('iam:sso_profiles.view_edit_create.recovery_email')
    },
    sessionTimeout: {
      sessionTimeout: t('iam:sso_profiles.view_edit_create.session_timeout')
    }
  }
  // for create flow
  const getMspText = (values) => {
    let organizationText = ''
    const mspOrganization = values?.includes(mspOptions.mspOrg)
    const customerOrganization = values?.includes(mspOptions.custOrg)
    switch (true) {
      case mspOrganization && customerOrganization:
        organizationText = t(
          'iam:sso_profiles.view_edit_create.msp_and_customer_organizations'
        )
        return organizationText
      case mspOrganization:
        organizationText = t(
          'iam:sso_profiles.view_edit_create.msp_organization'
        )
        return organizationText
      case customerOrganization:
        organizationText = t(
          'iam:sso_profiles.view_edit_create.customer_organizations'
        )
        return organizationText
      default:
        break
    }

    return organizationText
  }

  const getGeneralObject = () => {
    return {
      profileName: lodashGet(formValues, 'general.profileName'),
      domain:
        lodashGet(formValues, 'domain') === 'none'
          ? '--'
          : lodashGet(formValues, 'domain'),
      authorization: getAuthorizationText(
        lodashGet(formValues, 'general.authorizationMethod')
      ),
      ...(isMSP() && formValues.general?.msp_methods
        ? {
            applies: appliesToText
          }
        : {
            applies:
              lodashGet(formValues, 'domain') &&
              getMspText(lodashGet(formValues, 'msp_methods'))
          })
    }
  }

  const getGeneralLDObject = () => {
    return {
      profileName: lodashGet(formValues, 'general.profileName'),
      domain:
        lodashGet(formValues, 'domain') === 'none'
          ? '--'
          : lodashGet(formValues, 'domain'),
      authentication: getAuthentication(
        lodashGet(formValues, 'general.Authnoptions')
      ),
      authorization: getAuthoriration(
        lodashGet(formValues, 'general.oidauthorizationMethod')
      ),
      ...(isMSP() && {
        applies: getMspLdText(lodashGet(formValues, 'msp_methods'))
      })
    }
  }

  const getSamlAttributesObject = () => {
    return {
      email: lodashGet(formValues, 'attribute_mapping.email'),
      // country: lodashGet(formValues, 'attribute_mapping.countryCode'),
      first_name: lodashGet(formValues, 'attribute_mapping.firstName'),
      last_name: lodashGet(formValues, 'attribute_mapping.lastName'),
      ccs_attribute:
        lodashGet(formValues, 'general.authorizationMethod') ===
          ssoModeEnum.AUTHORIZATION ||
        lodashGet(formValues, 'general.oidauthorizationMethod') ===
          oidcauthorization.SSO
          ? lodashGet(formValues, 'attribute_mapping.ccsAttribute')
          : ''
    }
  }

  const getPkceValue = (value) => {
    let text = ''
    switch (true) {
      case value === true:
        text = 'Yes'
        return text
      case value === false:
        text = 'No'
        return text
      default:
        break
    }
    return text
  }
  const getOpenIdConnectConfiguration = () => {
    return {
      client: lodashGet(formValues, 'oidcIdpConfig.clientId'),
      usePkce: getPkceValue(lodashGet(formValues, 'oidcIdpConfig.usePkce')),
      issuer: lodashGet(formValues, 'oidcIdpConfig.issuer'),
      authorizationEndpoint: lodashGet(
        formValues,
        'oidcIdpConfig.authorizationEndpoint'
      ),
      tokenEndpoint: lodashGet(formValues, 'oidcIdpConfig.tokenEndpoint'),
      jwksEndpoint: lodashGet(formValues, 'oidcIdpConfig.jwksEndpoint'),
      userInfoEndpoint: lodashGet(formValues, 'oidcIdpConfig.userInfoEndpoint'),
      greenlakeRedirectUrl: generateRedirectUrl(
        lodashGet(formValues, 'oidcIdpConfig.issuer')
      )
    }
  }

  const getIdentityProviderConfigurationObject = () => {
    return {
      entityId: idp.entityIdUrl,
      acsUrl: idp.acsUrl,
      defaultRelayState: idp.defaultRelayStateUrl
    }
  }

  const getGreenLakeConfigurationObject = () => {
    return {
      entity_id: lodashGet(formValues, 'saml_idp_config.entity_id'),
      login_url: lodashGet(formValues, 'saml_idp_config.login_url'),
      logout_url: lodashGet(formValues, 'saml_idp_config.logout_url'),
      signing_certificate: lodashGet(
        formValues,
        'saml_idp_config.signing_certificate'
      )
    }
  }

  const getRecoveryUserObject = () => {
    return {
      recoveryEmail: lodashGet(formValues, 'recovery_user.recoveryEmail'),
      pocEmail: lodashGet(formValues, 'recovery_user.pocEmail')
    }
  }

  const getSessionTimeoutObject = () => {
    return {
      sessionTimeout: lodashGet(formValues, 'timeout')
    }
  }

  const getFilteredObject = (obj, filteredValues = []) => {
    const filteredObjectValues = {}
    Object.entries(obj).forEach(([key, value]) => {
      if (filteredValues.includes(key)) {
        filteredObjectValues.id = { ...filteredObjectValues.id, [key]: value }
      }
    })
    return filteredObjectValues.id
  }

  const general = renameKeys(
    tempI18NKeys.general,

    getFilteredObject(getGeneralObject(), Object.keys(tempI18NKeys.general))
  )

  const generalLD = renameKeys(
    tempI18NKeys.general_LDflag,
    getFilteredObject(
      getGeneralLDObject(),
      Object.keys(tempI18NKeys.general_LDflag)
    )
  )

  const samlAttributes = renameKeys(
    tempI18NKeys.samlAttributes,

    getFilteredObject(
      getSamlAttributesObject(),
      Object.keys(tempI18NKeys.samlAttributes)
    )
  )

  const openIDAttributes = renameKeys(
    tempI18NKeys.openidConnectConfiguration,
    getFilteredObject(
      getOpenIdConnectConfiguration(),
      Object.keys(tempI18NKeys.openidConnectConfiguration)
    )
  )

  const identityProviderConfiguration = renameKeys(
    tempI18NKeys.identityProviderConfiguration,

    getFilteredObject(
      getIdentityProviderConfigurationObject(),
      Object.keys(tempI18NKeys.identityProviderConfiguration)
    )
  )

  const greenLakeConfiguration = renameKeys(
    tempI18NKeys.greenLakeConfiguration,
    getFilteredObject(
      getGreenLakeConfigurationObject(),
      Object.keys(tempI18NKeys.greenLakeConfiguration)
    )
  )

  const recoveryUser = renameKeys(
    tempI18NKeys.recoveryUser,
    getFilteredObject(
      getRecoveryUserObject(),
      Object.keys(tempI18NKeys.recoveryUser)
    )
  )

  const sessionTimeout = renameKeys(
    tempI18NKeys.sessionTimeout,
    getFilteredObject(
      getSessionTimeoutObject(),
      Object.keys(tempI18NKeys.sessionTimeout)
    )
  )

  return (
    <Box gap="medium" pad={{ bottom: 'medium' }}>
      {isGlcpOidc ? (
        <Box gap="medium">
          <ObjectList
            data={generalLD}
            testId="review-general"
            title={t('iam:sso_profiles.view_edit_create.general')}
            showRuler={false}
          />
          {AuthenticationMode === 'Saml' && (
            <Box gap="medium">
              <ObjectList
                data={samlAttributes}
                testId="review-saml-attributes"
                title={t('iam:sso_profiles.view_edit_create.saml_attributes')}
                showRuler={false}
              />
              <ObjectList
                data={identityProviderConfiguration}
                testId="review-identity-provider-configuration"
                title={t(
                  'iam:sso_profiles.view_edit_create.identity_provider_configuration'
                )}
                showRuler={false}
              />
              <ObjectList
                data={greenLakeConfiguration}
                testId="review-greenlake-configuration"
                title={t(
                  'iam:sso_profiles.view_edit_create.greenlake_configuration'
                )}
                showRuler={false}
              />
            </Box>
          )}
          {AuthenticationMode === 'OIDC' && (
            <ObjectList
              data={openIDAttributes}
              testId="openid-connect-configuration"
              title={t('iam:sso_profiles_oidc.oidc_config_step_title')}
              showRuler={false}
            />
          )}
        </Box>
      ) : (
        <Box gap="medium">
          <ObjectList
            data={general}
            testId="review-general"
            title={t('iam:sso_profiles.view_edit_create.general')}
            showRuler={false}
          />
          <ObjectList
            data={samlAttributes}
            testId="review-saml-attributes"
            title={t('iam:sso_profiles.view_edit_create.saml_attributes')}
            showRuler={false}
          />
          <ObjectList
            data={identityProviderConfiguration}
            testId="review-identity-provider-configuration"
            title={t(
              'iam:sso_profiles.view_edit_create.identity_provider_configuration'
            )}
            showRuler={false}
          />
          <ObjectList
            data={greenLakeConfiguration}
            testId="review-greenlake-configuration"
            title={t(
              'iam:sso_profiles.view_edit_create.greenlake_configuration'
            )}
            showRuler={false}
          />
        </Box>
      )}
      <ObjectList
        data={recoveryUser}
        testId="review-recovery-user"
        title="Recovery user"
        showRuler={false}
      />
      <ObjectList
        data={sessionTimeout}
        testId="review-session-timeout"
        title={t('iam:sso_profiles.view_edit_create.session_timeout')}
        showRuler={false}
      />
    </Box>
  )
}

export default ReviewAndCreate
